import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import Sms from '../Graphics/Sms'
import styles from '../../../../css/inputs.module.css'

export const NumberInput = function NumberInput ({
  disabled,
  childChange,
  field,
  id,
  max = Number.MAX_VALUE,
  min = Number.MIN_VALUE,
  steps = '1',
  style,
  value,
}) {
  return (
    <input
      className={style}
      disabled={disabled}
      id={id}
      max={max}
      min={min}
      name={field}
      onChange={childChange}
      step={steps}
      type='number'
      value={value ?? ''}
    />
  )
}

export const DecimalInput = function DecimalInput ({
  childChange,
  onFocus,
  disabled,
  field,
  max = Number.MAX_VALUE,
  min = Number.MIN_VALUE,
  onInput,
  style,
  value,
}) {
  return (
    <input
      className={style}
      disabled={disabled}
      max={max}
      min={min}
      name={field}
      onChange={childChange}
      onInput={onInput}
      step='any'
      onFocus={onFocus}
      type='number'
      value={value ?? ''}
    />
  )
}

export const TextInput = ({
  autoFocus,
  defaultValue,
  disabled,
  field,
  pattern,
  required,
  style,
  value,
  placeholder,
  childChange
}) => {
  const handleChange = (event) => {
    if (typeof childChange === 'function') {
      childChange(event)
    }
  }

  // Use defaultValue for uncontrolled, or value for controlled - not both
  // If value is provided, use controlled input. Otherwise use defaultValue if provided.
  const inputProps = value !== undefined
    ? { value: value ?? '' }
    : defaultValue !== undefined
      ? { defaultValue }
      : { value: '' }

  return (
    <input
      autoFocus={autoFocus}
      className={style}
      disabled={disabled}
      name={field}
      onChange={handleChange}
      pattern={pattern}
      required={required}
      placeholder={placeholder}
      type='text'
      {...inputProps}
    />
  )
}

export const EmailInput = ({ childChange, disabled, field, style, value }) => {
  return (
    <input
      className={style}
      disabled={disabled}
      name={field}
      onChange={childChange}
      pattern='[^ @]*@[^ @]*'
      type='email'
      value={value ?? ''}
    />
  )
}

export const PasswordInput = function PasswordInput ({
  childChange,
  disabled,
  field,
  style,
  value,
}) {
  return (
    <input
      className={style}
      disabled={disabled}
      name={field}
      onChange={childChange}
      type='password'
      value={value ?? ''}
    />
  )
}

// PinInput component using forwardRef so parents can call focusInput()
export const PinInput = forwardRef(({ autoFocus, childChange, field, value, style }, ref) => {
  const numberInputRef = useRef(null)

  const focusInput = () => {
    if (numberInputRef.current) {
      numberInputRef.current.focus()
    }
  }

  useImperativeHandle(ref, () => ({
    focusInput,
  }))

  return (
    <input
      ref={numberInputRef}
      type='number'
      inputMode='numeric'
      pattern='[0-9]*'
      maxLength='1'
      value={value}
      name={field}
      onChange={childChange}
      className={style}
      autoFocus={autoFocus}
      autoComplete='new-password'
    />
  )
})
PinInput.propTypes = {
  autoFocus: PropTypes.bool,
  value: PropTypes.node,
  field: PropTypes.string,
  childChange: PropTypes.func,
  style: PropTypes.string,
}

const isValidTimeValue = (time) => (
  typeof time === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(time)
)

const normalizeTimeValue = (time) => (isValidTimeValue(time) ? time : '')

export const TimeInput = function TimeInput ({ value, field, childChange, style }) {
  const [draftValue, setDraftValue] = useState(() => normalizeTimeValue(value))
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (!isEditing) {
      const normalized = normalizeTimeValue(value)
      if (normalized !== draftValue) {
        setDraftValue(normalized)
      }
    }
  }, [value, isEditing, draftValue])

  const handleChange = (event) => {
    const nextValue = event.target.value
    setDraftValue(nextValue)
    if (isValidTimeValue(nextValue) && typeof childChange === 'function') {
      childChange(event)
    }
  }

  const handleBlur = () => {
    setIsEditing(false)
    if (!isValidTimeValue(draftValue)) {
      setDraftValue(normalizeTimeValue(value))
    }
  }

  const handleFocus = () => {
    setIsEditing(true)
  }

  return (
    <input
      className={style}
      type='time'
      value={draftValue}
      name={field}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  )
}

export const SelectInput = function SelectInput ({
  childChange,
  disabled,
  field,
  options,
  style,
  value,
  defaultValue,
  selected,
  caret = false,
}) {
  const optionsArr = options.map((x) => (
    <SelectKey
      key={`${x.name}_${x.value}`}
      value={x.value}
      name={x.name}
      style={style}
      disabled={x.disabled}
      selected={x.selected}
    />
  ))
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <select
        className={style}
        disabled={disabled}
        name={field}
        onChange={childChange}
        value={value}
        defaultValue={defaultValue}
        style={{ backgroundColor: 'inherit', border: 'none', width: '100%' }}
      >
        {optionsArr}
      </select>
      {caret
        ? (
          <div
            style={{
              background: 'inherit',
              color: 'inherit',
              right: '5px',
              top: '0',
              height: '34px',
              padding: '0px',
              position: 'absolute',
              pointerEvents: 'none',
            }}
          >
            <i className='fa fa-caret-down' />
          </div>
          )
        : ''}
    </div>
  )
}

export const SelectKey = function SelectKey ({
  style,
  value,
  name,
  disabled,
  selected,
}) {
  return <option className={style} value={value} selected={selected} disabled={disabled}>{name}</option>
}

export const ButtonList = function ButtonList ({
  value,
  field,
  childChange,
  options,
  disabled,
}) {
  const optionsArr = []
  for (let i = 0; i < options.length; i += 1) {
    if (value === '' || value === options[i].value) {
      optionsArr.push(
        <ButtonListKey
          key={i}
          value={options[i].value}
          icon={options[i].icon || (<Sms />)}
          name={options[i].name}
          field={field}
          childChange={childChange}
          selected={value}
          disabled={disabled}
        />
      )
    }
  }
  return optionsArr
}

export const ButtonListKey = function ButtonListKey ({
  icon,
  value,
  name,
  field,
  selected,
  childChange,
  disabled,
}) {
  return (
    <label className={styles.buttonList}>
      <div className={styles.buttonListIconContainer}>
        {icon}
      </div>
      <input
        type='radio'
        name={field}
        value={value}
        onChange={childChange}
        checked={selected === value}
        disabled={disabled}
      />
      <h4 className={styles.buttonListText}>{name}</h4>
    </label>
  )
}

export const RadioList = function RadioList ({
  value,
  field,
  childChange,
  options,
  disabled,
}) {
  const optionsArr = []
  for (let i = 0; i < options.length; i++) {
    optionsArr.push(
      <RadioListKey
        key={i}
        value={options[i].value}
        name={options[i].name}
        field={field}
        childChange={childChange}
        selected={value}
        disabled={disabled}
      />
    )
  }
  return optionsArr
}

export const RadioListKey = function RadioListKey ({
  value,
  name,
  field,
  selected,
  childChange,
  disabled,
}) {
  return (
    <label className={`${styles.radioList} ${selected == value ? styles.radioChecked : ''}`}>
      <input
        type='radio'
        name={field}
        value={value}
        onChange={childChange}
        checked={selected == value}
        disabled={disabled}
      />
      <h4 className={styles.buttonListText}>{name}</h4>
    </label>
  )
}

export const isNumber = (x) => !isNaN(x) && isFinite(x)

// Re-exported from the shared validation module so existing callers (this
// file's named export of isEmail) keep working unchanged. New callers should
// prefer importing directly from the shared module
// (app-web/app/util/validation/email) using a relative path appropriate for
// their location — there is no `app` path alias configured in this project.
export { isEmail } from '../../../util/validation/email'
